import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart3,
  Users,
  TrendingUp,
  Target,
  Calendar,
  Download,
  Filter
} from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface HRAnalyticsData {
  employeeGrowth: Array<{
    month: string
    count: number
    change: number
  }>
  departmentDistribution: Array<{
    department: string
    count: number
    percentage: number
  }>
  turnoverRate: number
  averageTenure: number
  hiringMetrics: {
    totalHires: number
    averageTimeToHire: number
    topSources: Array<{ source: string; count: number }>
  }
  diversityMetrics: {
    genderRatio: { male: number; female: number; other: number }
    ageGroups: Array<{ group: string; count: number }>
  }
}

const HRAnalytics: React.FC = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Start of year
    endDate: new Date().toISOString().split('T')[0]
  })
//   const [selectedMetric, setSelectedMetric] = useState('overview')

  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['hr-analytics', dateRange],
    queryFn: async (): Promise<HRAnalyticsData> => {
      const response = await api.get('/reports/hr-analytics', {
        params: dateRange
      })
      return response.data.data
    }
  })

  const metricCards = [
    {
      title: 'Total Employees',
      value: analyticsData?.employeeGrowth[analyticsData.employeeGrowth.length - 1]?.count || 0,
      change: analyticsData?.employeeGrowth[analyticsData.employeeGrowth.length - 1]?.change || 0,
      icon: Users,
      color: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
    },
    {
      title: 'Turnover Rate',
      value: `${analyticsData?.turnoverRate || 0}%`,
      change: -2.1,
      icon: TrendingUp,
      color: 'bg-rose-100 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400'
    },
    {
      title: 'Avg Tenure',
      value: `${analyticsData?.averageTenure || 0} yrs`,
      change: 0.5,
      icon: Calendar,
      color: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
    },
    {
      title: 'Time to Hire',
      value: `${analyticsData?.hiringMetrics.averageTimeToHire || 0} days`,
      change: -3,
      icon: Target,
      color: 'bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
    }
  ]

  const handleExport = async (format: 'csv' | 'pdf') => {
    try {
      const response = await api.get(`/reports/hr-analytics/export`, {
        params: { ...dateRange, format },
        responseType: 'blob'
      })

      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `hr-analytics-${new Date().toISOString().split('T')[0]}.${format}`
      link.click()
      window.URL.revokeObjectURL(url)

      toast.success(`HR Analytics exported as ${format.toUpperCase()}`)
    } catch (error) {
      toast.error('Failed to export report')
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-600 text-white">
            <BarChart3 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">HR Analytics</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Comprehensive workforce analytics and insights</p>
          </div>
        </div>
        <div className="flex items-center space-x-4 mt-4 lg:mt-0">
          <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
            <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="text-sm border-none focus:outline-none bg-transparent text-gray-900 dark:text-white"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="text-sm border-none focus:outline-none bg-transparent text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handleExport('csv')}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>CSV</span>
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {metricCards.map((card, index) => {
          const Icon = card.icon
          return (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{card.value}</p>
                  <div className={`flex items-center mt-2 text-sm ${card.change >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    <TrendingUp className={`h-4 w-4 mr-1 ${card.change < 0 ? 'rotate-180' : ''}`} />
                    {card.change >= 0 ? '+' : ''}{card.change}%
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Department Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Department Distribution</h3>
          <div className="space-y-4">
            {analyticsData?.departmentDistribution.map((dept, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{dept.department}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{ width: `${dept.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600 dark:text-gray-400 w-12 text-right">{dept.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hiring Metrics */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Hiring Metrics</h3>
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Hires</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {analyticsData?.hiringMetrics.totalHires || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Average Time to Hire</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {analyticsData?.hiringMetrics.averageTimeToHire || 0} days
              </span>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Top Hiring Sources</h4>
              <div className="space-y-2">
                {analyticsData?.hiringMetrics.topSources.map((source, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{source.source}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{source.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Diversity Metrics */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Diversity Overview</h3>
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Gender Distribution</h4>
              <div className="flex space-x-4">
                {analyticsData?.diversityMetrics.genderRatio && Object.entries(analyticsData.diversityMetrics.genderRatio).map(([gender, count]) => (
                  <div key={gender} className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{count}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">{gender}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Age Groups</h4>
              <div className="space-y-2">
                {analyticsData?.diversityMetrics.ageGroups.map((group, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{group.group}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{group.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Employee Growth Chart Placeholder */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Employee Growth</h3>
          <div className="flex items-center justify-center h-48 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <BarChart3 className="h-12 w-12 text-gray-400" />
            <span className="ml-2 text-gray-500 dark:text-gray-400">Growth chart visualization</span>
          </div>
        </div>
      </div>

      {/* Insights Section */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-5">
        <h3 className="text-base font-semibold text-indigo-900 dark:text-indigo-300 mb-3">Key Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-indigo-800 dark:text-indigo-400">
          <div>Employee growth rate: +{analyticsData?.employeeGrowth[analyticsData.employeeGrowth.length - 1]?.change || 0}% this period</div>
          <div>Turnover rate below industry average by 1.2%</div>
          <div>IT department shows highest growth at 25% of workforce</div>
          <div>Referrals remain top hiring source with 35% success rate</div>
        </div>
      </div>
    </div>
  )
}

export default HRAnalytics